import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Wrench, Clock, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '',
  branch_ids: [],
  all_services: true,
  service_ids: [],
  unit_type: 'adet',
  inactive_days: '',
  max_amount: '',
  service_amounts: {},
};

const ServiceWarningSettings = ({ readOnly = false }) => {
  const [rules, setRules] = useState([]);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json',
  });

  const loadRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/purchasing/warning-rules?rule_type=hizmet_hakedis`, { headers: getAuthHeaders() });
      setRules(res.data || []);
    } catch (e) {
      console.error('Hizmet hak ediş uyarı kuralları yüklenemedi:', e);
    }
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() });
      setBranches(res.data || []);
    } catch (e) {
      console.error('Şubeler yüklenemedi:', e);
    }
  }, []);

  useEffect(() => {
    loadRules();
    loadBranches();
  }, [loadRules, loadBranches]);

  // Seçili şubelerin hizmetlarını yükle
  useEffect(() => {
    const fetchSubcontractors = async () => {
      if (!form.branch_ids || form.branch_ids.length === 0) {
        setServices([]);
        return;
      }
      try {
        // Fetch all services
        const response = await axios.get(`${API}/api/purchasing/services`, { headers: getAuthHeaders() });
        const allSubs = response.data || [];
        
        // Filter by selected branches
        const filtered = allSubs.filter(sub => 
          form.branch_ids.includes(sub.branch_id)
        );
        
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
        setServices(filtered);
      } catch (e) {
        console.error('Alınan Hizmetler yüklenemedi:', e);
      }
    };
    fetchSubcontractors();
  }, [form.branch_ids]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name || '',
      branch_ids: rule.branch_ids || [],
      all_services: rule.all_services !== false,
      service_ids: rule.service_ids || [],
      unit_type: rule.unit_type || 'adet',
      inactive_days: rule.inactive_days ?? '',
      max_amount: rule.max_amount ?? '',
      service_amounts: rule.service_amounts || {},
    });
    setModalOpen(true);
  };

  const toggleBranch = (bid) => {
    setForm((prev) => {
      const exists = prev.branch_ids.includes(bid);
      const branch_ids = exists ? prev.branch_ids.filter((x) => x !== bid) : [...prev.branch_ids, bid];
      return { ...prev, branch_ids };
    });
  };

  const toggleService = (sid) => {
    setForm((prev) => {
      const exists = prev.service_ids.includes(sid);
      const service_ids = exists ? prev.service_ids.filter((x) => x !== sid) : [...prev.service_ids, sid];
      return { ...prev, service_ids };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Lütfen ayar ismi girin');
      return;
    }
    if (form.branch_ids.length === 0) {
      toast.error('En az bir şube seçmelisiniz');
      return;
    }
    if (!form.all_services && form.service_ids.length === 0) {
      toast.error('En az bir ürün seçin veya "Tüm Alınan Hizmetler" seçeneğini işaretleyin');
      return;
    }
    const payload = {
      rule_type: 'hizmet_hakedis',
      name: form.name.trim(),
      branch_ids: form.branch_ids,
      all_services: form.all_services,
      service_ids: form.all_services ? [] : form.service_ids,
      unit_type: form.unit_type,
      inactive_days: form.inactive_days === '' ? null : Number(form.inactive_days),
      max_amount: form.max_amount === '' ? null : Number(form.max_amount),
        service_amounts: form.service_amounts || {},
    };
    setLoading(true);
    try {
      if (editingId) {
        await axios.put(`${API}/api/purchasing/warning-rules/${editingId}`, payload, { headers: getAuthHeaders() });
        toast.success('Hizmet hak ediş uyarı ayarı güncellendi');
      } else {
        await axios.post(`${API}/api/purchasing/warning-rules`, payload, { headers: getAuthHeaders() });
        toast.success('Hizmet hak ediş uyarı ayarı oluşturuldu');
      }
      setModalOpen(false);
      loadRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`"${rule.name}" ayarını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/api/purchasing/warning-rules/${rule.id}`, { headers: getAuthHeaders() });
      toast.success('Ayar silindi');
      loadRules();
    } catch (e) {
      toast.error('Silme başarısız');
    }
  };

  const branchName = (bid) => branches.find((b) => b.id === bid)?.name || bid;
  const unitSuffix = (t) => ('adet');

  return (
    <div className="space-y-4" data-testid="subcontractor-warning-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Alınan Hizmetler Hak Ediş Uyarı Ayarları</h3>
          <p className="text-sm text-gray-500">Şube ve hizmet bazında hak ediş eşiklerini tanımlayın.</p>
        </div>
        {!readOnly && (
          <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700" data-testid="add-subcontractor-rule-btn">
            <Plus className="h-4 w-4 mr-2" /> Yeni Alınan Hizmetler Hak Ediş Uyarı Ayarı
          </Button>
        )}
      </div>

      {/* Kural Listesi */}
      {rules.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-14 text-center">
          <Wrench className="mb-3 h-10 w-10 text-gray-300" />
          <p className="text-base font-medium text-gray-700">Henüz hizmet hak ediş uyarı ayarı yok</p>
          <p className="mt-1 text-sm text-gray-400">Yeni bir hizmet hak ediş uyarı ayarı ekleyerek başlayın.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="subcontractor-rules-list">
          {rules.map((rule) => (
            <div key={rule.id} className="rounded-xl border border-gray-200 bg-white p-4" data-testid={`subcontractor-rule-${rule.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">{rule.name}</span>
                    <Badge variant="outline" className="text-[11px]">
                      <Clock className="h-3 w-3 mr-1" />
                      Hizmet
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="text-gray-500 flex items-center gap-1"><Building2 className="h-3 w-3" /> Şubeler:</span>
                    {(rule.branch_ids || []).map((bid) => (
                      <Badge key={bid} className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[11px]">{branchName(bid)}</Badge>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="text-gray-500 flex items-center gap-1"><Wrench className="h-3 w-3" /> Alınan Hizmetler:</span>
                    {rule.all_services ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[11px]">Tüm Alınan Hizmetler</Badge>
                    ) : (
                      <span className="text-gray-700">{(rule.service_ids || []).length} hizmet</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-600 pt-1">
                    <span>Hareketsizlik Süresi: <b>{rule.inactive_days ?? '-'}{rule.inactive_days != null ? ' Gün' : ''}</b></span>
                    <span>Aşım Limiti: <b>{rule.max_amount ?? '-'}{rule.max_amount != null ? ' TL' : ''}</b></span>
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(rule)} data-testid={`edit-subcontractor-rule-${rule.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(rule)} data-testid={`delete-subcontractor-rule-${rule.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ekle/Düzenle Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inactive-rule-modal">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Alınan Hizmetler Hak Ediş Uyarı Ayarını Düzenle' : 'Yeni Alınan Hizmetler Hak Ediş Uyarı Ayarı'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* İsim */}
            <div className="space-y-2">
              <Label>Ayar İsmi *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="örn. Kritik Malzeme Gecikmesi"
                data-testid="purchase-rule-name"
              />
            </div>

            {/* Şubeler */}
            <div className="space-y-2">
              <Label>Şubeler * <span className="text-xs text-gray-400">(bakılacak şubeleri seçin)</span></Label>
              <div className="border rounded-md p-3 max-h-40 overflow-y-auto grid grid-cols-2 gap-2" data-testid="inactive-rule-branches">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.branch_ids.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} data-testid={`branch-check-${b.id}`} />
                    {b.name}
                  </label>
                ))}
              </div>
              {form.branch_ids.length > 0 && <p className="text-xs text-gray-500">{form.branch_ids.length} şube seçildi</p>}
            </div>

            {/* Alınan Hizmetler */}
            <div className="space-y-2">
              <Label>Alınan Hizmetler</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.all_services} onCheckedChange={(v) => setForm((p) => ({ ...p, all_services: !!v }))} data-testid="all-services-check" />
                  Tüm Alınan Hizmetler
                </label>
              </div>
              {!form.all_services && (
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2" data-testid="inactive-rule-services">
                  {form.branch_ids.length === 0 ? (
                    <p className="text-sm text-gray-400">Önce şube seçin</p>
                  ) : services.length === 0 ? (
                    <p className="text-sm text-gray-400">Seçili şubelerde hizmet bulunamadı</p>
                  ) : (
                                        services.map((s) => {
                      const isSelected = form.service_ids.includes(s.id);
                      return (
                        <div key={s.id} className="flex items-center justify-between py-1 border-b last:border-0 hover:bg-slate-50 rounded px-1 transition-colors">
                          <label className="flex items-center gap-2 text-sm cursor-pointer flex-1">
                            <Checkbox 
                              checked={isSelected} 
                              onCheckedChange={() => toggleService(s.id)} 
                              data-testid={`subcontractor-check-${s.id}`} 
                            />
                            <span className="truncate">{s.service_name || s.name}</span>
                          </label>
                          {isSelected && (
                            <div className="w-32 ml-2">
                              <Input
                                type="number"
                                min="0"
                                placeholder="Özel Tutar (TL)"
                                value={form.service_amounts?.[s.id] || ''}
                                onChange={(e) => setForm(p => ({
                                  ...p, 
                                  service_amounts: {
                                    ...(p.service_amounts || {}), 
                                    [s.id]: e.target.value === '' ? null : Number(e.target.value)
                                  }
                                }))}
                                className="h-7 text-xs"
                                data-testid={`subcontractor-amount-${s.id}`}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
              {!form.all_services && form.service_ids.length > 0 && (
                <p className="text-xs text-gray-500">{form.service_ids.length} hizmet seçildi</p>
              )}
            </div>

            {/* Eşikler */}
                        <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hareketsizlik Süresi (Gün)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.inactive_days}
                  onChange={(e) => setForm((p) => ({ ...p, inactive_days: e.target.value }))}
                  placeholder={'Örn: 30'}
                  data-testid="inactive-days-input"
                />
                <p className="text-[10px] text-gray-500">Bu kadar süre hakediş yapılmazsa uyarır.</p>
              </div>
              <div className="space-y-2">
                <Label>Hizmet Tutarını Aşma Limiti (TL)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.max_amount}
                  onChange={(e) => setForm((p) => ({ ...p, max_amount: e.target.value }))}
                  placeholder={'Örn: 0 veya 5000'}
                  data-testid="max-amount-input"
                />
                <p className="text-[10px] text-gray-500">Yapılan hakedişler, anlaşılan hizmet tutarını belirtilen TL kadar aşarsa uyarır (0 girerseniz direkt tutar aşıldığında uyarır).</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Vazgeç</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-amber-600 hover:bg-amber-700" data-testid="save-inactive-rule-btn">
              {editingId ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceWarningSettings;
